import React from "react";
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Collapse,
  IconButton,
  useTheme,
} from "@mui/material";
import { ExpandLess, ExpandMore } from "@mui/icons-material";
import MediaAssetsUpload from "../../../../../views/components/common/upload/MediaAssetsUpload";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import EyeIcon from "../../../../../assets/eyeframe.png";
import { ImageData } from "../../../../../models/Image/Image";

interface PodProductMediaAssetsCardProps {
  open: boolean;
  onToggle: () => void;
  existingUrls: string[];
  newFiles: File[];
  initialUrls: string[];
  saving: boolean;
  hasChanges: boolean;
  onDeleteExisting: (index: number) => void;
  onRemoveNew: (image: { id: string | number; title?: string; img: string }) => void;
  onAddNew: (files: File[]) => void;
  onReset: () => void;
  onSave: () => void;
}

const PodProductMediaAssetsCard: React.FC<PodProductMediaAssetsCardProps> = ({
  open,
  onToggle,
  existingUrls,
  newFiles,
  saving,
  hasChanges,
  onDeleteExisting,
  onRemoveNew,
  onAddNew,
  onReset,
  onSave,
}) => {
  const theme = useTheme();

  return (
    <Card sx={{ marginTop: 2, marginBottom: 2 }}>
      <CardHeader
        onClick={onToggle}
        title={
          <Box display="flex" alignItems="center" gap={1}>
            <img src={EyeIcon} alt="Media" style={{ width: 30, height: 30 }} />
            <CustomTypography variant="heading5">Media & Assets</CustomTypography>
            <Chip
              label="Gallery"
              size="small"
              sx={{
                ml: 1,
                borderRadius: 2,
                backgroundColor: theme.palette.customColors.white,
                color: theme.palette.customColors.greyTextColor,
                border: `1px solid ${theme.palette.customColors.greyBackground}`,
                height: "20px",
                fontSize: "0.65rem",
                "& .MuiChip-label": { px: 0.8, py: 0 },
              }}
            />
          </Box>
        }
        action={
          <Box display="flex" alignItems="center" gap={1}>
            <Box sx={{ display: { xs: "none", sm: "block" } }}>
              <CustomTypography variant="body2" color="text.secondary">
                1 field
              </CustomTypography>
            </Box>
            <IconButton>{open ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
        }
        sx={{
          cursor: "pointer",
          px: 3,
          py: 2,
          borderBottom: `1px solid ${theme.palette.customColors.greyBackground}`,
          "&:hover": { backgroundColor: theme.palette.customColors.greyBackground },
        }}
      />
      <Collapse in={open} timeout="auto" unmountOnExit>
        <CardContent sx={{ px: 4 }}>
          <MediaAssetsUpload
            existingImageList={existingUrls.map((url, idx) => ({
              id: idx + 1,
              img: url,
              title: `Image ${idx + 1}`,
            } as ImageData))}
            newImageList={newFiles}
            deleteImage={(image: ImageData) => {
              const imageId = Number(image.id);
              if (!Number.isFinite(imageId) || imageId <= 0) return;
              onDeleteExisting(imageId - 1);
            }}
            removeImage={onRemoveNew}
            addImagesToBeUploaded={onAddNew}
          />
          <Box
            sx={{
              mt: 2.5,
              display: "flex",
              justifyContent: "flex-end",
              gap: 1.5,
            }}
          >
            <AppButton
              label="Reset"
              variant="grayscale"
              onClick={onReset}
              disabled={saving}
              customStyles={{ minWidth: 120 }}
            />
            <AppButton
              label={saving ? "Saving..." : "Save Media"}
              variant="primary"
              onClick={onSave}
              disabled={saving || !hasChanges}
              customStyles={{ minWidth: 130 }}
            />
          </Box>
        </CardContent>
      </Collapse>
    </Card>
  );
};

export default PodProductMediaAssetsCard;
